import { useEffect, useMemo, useRef } from 'react';
import type { HistoryRecord, Screen } from '../types';
import { formatTime } from '../types';
import { useGameStore } from '../store';
import { ParallaxBg } from './ParallaxBg';
import { Heading, Muted, Caption, Panel, Button } from './ui';
import { playClick, ensureAudioContext } from '../sounds';
import './History.css';

interface HistoryProps {
  onNavigate: (screen: Screen) => void;
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Calendar-relative label: "Today", "Yesterday", weekday within a week,
 *  then short date ("Apr 11") or full date with year for older entries. */
function formatTimestamp(ts: number): string {
  if (!ts) return '';
  const date = new Date(ts);
  const now = new Date();
  const dayDiff = Math.round(
    (startOfDay(now) - startOfDay(date)) / 86_400_000,
  );

  if (dayDiff <= 0) return 'Today';
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff < 7) {
    return date.toLocaleDateString(undefined, { weekday: 'long' });
  }
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  });
}

/* -------------------- Thumbnail -------------------- */

/** Small pixel-art thumbnail of a solved puzzle's solution grid. */
function SolutionThumb({ solution }: { solution: boolean[][] }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rows = solution.length;
  const cols = solution[0]?.length ?? 0;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (w === 0 || h === 0) return;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    const cw = w / cols;
    const ch = h / rows;
    ctx.fillStyle = '#4a2008';
    for (let r = 0; r < rows; r++) {
      const row = solution[r];
      if (!row) continue;
      for (let c = 0; c < cols; c++) {
        if (row[c]) ctx.fillRect(c * cw, r * ch, cw, ch);
      }
    }
  }, [solution, rows, cols]);

  return (
    <div
      className="history-thumb"
      style={{ aspectRatio: `${cols} / ${rows}` }}
    >
      <canvas ref={canvasRef} className="history-thumb__canvas" />
    </div>
  );
}

/* -------------------- Row -------------------- */

function HistoryRow({
  entry,
  onOpen,
  onReplay,
}: {
  entry: HistoryRecord;
  onOpen: () => void;
  onReplay: () => void;
}) {
  const time = formatTime(entry.seconds);
  const hints = entry.hints ?? 0;
  const hintsLabel =
    hints > 0 ? `${hints} hint${hints === 1 ? '' : 's'}` : 'no hints';
  return (
    <Panel className="history-row history-row--completed">
      <SolutionThumb solution={entry.solution} />
      <div className="history-row__body">
        <Heading as="h3" className="history-row__label">
          {entry.label}
        </Heading>
        <Muted className="history-row__meta">
          {formatTimestamp(entry.timestamp)}
        </Muted>
        <Caption className="history-row__stats">
          {`Time ${time}`}
          {' · '}
          {hintsLabel}
        </Caption>
      </div>
      <div className="history-row__actions">
        <Button
          variant="ghost"
          size="wide-sm"
          className="history-action-btn"
          onClick={onOpen}
        >
          Open
        </Button>
        <Button
          variant="ghost"
          size="wide-sm"
          className="history-action-btn"
          onClick={onReplay}
        >
          Replay
        </Button>
      </div>
    </Panel>
  );
}

/* -------------------- Screen -------------------- */

export function History({ onNavigate }: HistoryProps) {
  const history = useGameStore((s) => s.history);
  const clearGameState = useGameStore((s) => s.clearGameState);

  const entries = useMemo(
    () =>
      Object.values(history).sort((a, b) => b.timestamp - a.timestamp),
    [history],
  );

  const navigate = (screen: Screen) => {
    ensureAudioContext();
    playClick();
    onNavigate(screen);
  };

  const handleOpen = (entry: HistoryRecord) => navigate(entry.screen);
  const handleReplay = (entry: HistoryRecord) => {
    clearGameState(entry.storageKey);
    navigate({ ...entry.screen, entry: 'fresh' } as Screen);
  };

  return (
    <div className="history-screen">
      <ParallaxBg className="game-bg" />
      <div className="history-content">
        <Heading className="history-title">History</Heading>

        {entries.length === 0 ? (
          <Caption className="history-empty">
            No levels completed yet. Finish a level and it will show up here.
          </Caption>
        ) : (
          <div className="history-list">
            {entries.map((entry) => (
              <HistoryRow
                key={entry.id}
                entry={entry}
                onOpen={() => handleOpen(entry)}
                onReplay={() => handleReplay(entry)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
